import React from 'react';
import {
  Header4,
  SimpleBlock,
  Text,
  TextLink,
  FormTemplate,
  SubmitButton,
  BaseInputProps,
  useFormContextRequired,
  useInputHelper, NewLine,
} from 'ui-builder';
import { usePopupFormManager, InformationMessage, Popup } from 'features/nekst-widgets';
import {
  useBulkContactsImportService,
  ImportStatistics,
  BulkContactsImportResponse,
  parseStatistics,
  downloadCsv,
} from 'nekst-api';
import BaseInputLayoutWidget from '../../shared/uibuilder/form/layout/BaseInputLayoutWidget';

interface BulkImportFormData {
  inputCsv: string;
  fileName: string | null;
}

function CsvFileInput(props: BaseInputProps) {
  const inputHelper = useInputHelper(props);
  const formContext = useFormContextRequired();

  const readFileAsText = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        resolve(text);
      };
      reader.onerror = (e) => {
        reject(e);
      };
      reader.readAsText(file);
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      if (!file.name.endsWith('.csv')) {
        formContext.onChangeCallback({
          [props.source]: null,
          fileName: null,
        });
        alert('Please select a valid CSV file');
        event.target.value = '';
        return;
      }

      const csvContent = await readFileAsText(file);
      formContext.onChangeCallback({
        [props.source]: csvContent,
        fileName: file.name,
      });
    } else {
      formContext.onChangeCallback({
        [props.source]: null,
        fileName: null,
      });
    }
  };

  return (
    <BaseInputLayoutWidget {...inputHelper.getBaseInputLayoutProps()}>
      <input
        type="file"
        accept=".csv,text/csv,application/csv"
        onChange={handleFileChange}
      />
    </BaseInputLayoutWidget>
  );
}

function SelectedFileName() {
  const formContext = useFormContextRequired<BulkImportFormData>();

  if (!formContext.data || !formContext.data.fileName) {
    return null;
  }

  return (
    <SimpleBlock>
      <Text>Selected file: {formContext.data.fileName}</Text>
    </SimpleBlock>
  );
}

function ImportResultsView(props: {
  results: BulkContactsImportResponse;
  statistics: ImportStatistics;
}) {
  const { results, statistics } = props;
  const hasFailures = statistics.failed > 0 || statistics.skipped > 0;

  const handleDownloadFullCsv = () => {
    downloadCsv(results.fullCsv, 'contacts-import-full.csv');
  };

  const handleDownloadFailedCsv = () => {
    downloadCsv(results.failedCsv, 'contacts-import-failed.csv');
  };

  return (
    <SimpleBlock>
      <SimpleBlock>
        <Header4>Import Statistics</Header4>
      </SimpleBlock>

      <SimpleBlock>
        <Text>Total rows processed: {statistics.total}</Text>
      </SimpleBlock>

      <SimpleBlock>
        <Text>Successfully imported: {statistics.successful}</Text>
      </SimpleBlock>

      {statistics.failed > 0 && (
        <SimpleBlock>
          <Text>Failed: {statistics.failed}</Text>
        </SimpleBlock>
      )}

      {statistics.skipped > 0 && (
        <SimpleBlock>
          <Text>Skipped (duplicates): {statistics.skipped}</Text>
        </SimpleBlock>
      )}

      <SimpleBlock>
        <Header4>Download Results</Header4>
      </SimpleBlock>

      <SimpleBlock>
        <TextLink onClick={handleDownloadFullCsv}>
          Download Full Results CSV
        </TextLink>
      </SimpleBlock>

      {hasFailures && (
        <SimpleBlock>
          <TextLink onClick={handleDownloadFailedCsv}>
            Download Failed Rows CSV
          </TextLink>
        </SimpleBlock>
      )}

      <SimpleBlock>
        {hasFailures && (
          <>
            <NewLine />
            <InformationMessage>
              Review the failed rows CSV to see what went wrong and try importing those contacts again
              after fixing the issues.
            </InformationMessage>
          </>
        )}
      </SimpleBlock>
    </SimpleBlock>
  );
}

export default function BulkImportForm() {
  const bulkImportService = useBulkContactsImportService();
  const popupManager = usePopupFormManager();

  const handleDownloadTemplate = async () => {
    try {
      await bulkImportService.downloadTemplate();
    } catch (error) {
      alert('Failed to download template. Please try again.');
      console.error(error);
    }
  };

  const handleSubmit = async (data: BulkImportFormData): Promise<BulkContactsImportResponse> => {

    return await bulkImportService.importContacts(data.inputCsv);
  };

  const handleAfterSubmit = async (response: BulkContactsImportResponse): Promise<void> => {
    const stats = parseStatistics(response.fullCsv);

    popupManager.openForm(
      <Popup
        title="Import Complete"
        content={<ImportResultsView
          results={response}
          statistics={stats}
        />}
      />
    )
  };


  return (
    <FormTemplate<BulkImportFormData, BulkContactsImportResponse>
      title="Import Contacts from CSV"
      submitFormFunc={handleSubmit}
      afterSubmitFunc={handleAfterSubmit}
      validationSchema={{
        inputCsv: {
          type: 'string',
          constraints: {
            required: true,
          },
        },
      }}
      buttons={
        <SubmitButton text="Import" />
      }
    >
      <SimpleBlock>
        <Text>
          Upload a CSV file to import multiple contacts at once. The file must include firstName
          and lastName columns.
        </Text>
      </SimpleBlock>

      <SimpleBlock>
        <TextLink onClick={handleDownloadTemplate}>
          Download CSV Template
        </TextLink>
        {' '}
        (with all possible columns)
      </SimpleBlock>

      <SimpleBlock>
        <InformationMessage>
          When saving from Excel, use &apos;CSV (Comma delimited)&apos; format, not &apos;CSV (Semicolon
          delimited)&apos;
        </InformationMessage>
      </SimpleBlock>

      <SimpleBlock>
        <CsvFileInput source="inputCsv" label="CSV File" />
        <SelectedFileName />
      </SimpleBlock>
    </FormTemplate>
  );
}

export function useBulkImportForm() {
  const popupManager = usePopupFormManager();

  const open = () => {
    popupManager.openForm(<BulkImportForm />);
  };

  return {
    open,
  };
}
